import React from "react";
import { FaChevronRight, FaFlagCheckered } from "react-icons/fa";

function OptionButton({ text, onClick, state }) {
  // state: "idle" | "correct" | "wrong" | "disabled"
  const base =
    "w-full rounded-xl px-4 py-3 text-sm sm:text-base font-semibold transition border";
  const styles = {
    idle:
      "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800",
    correct:
      "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800",
    wrong:
      "bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800",
    disabled:
      "bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700 cursor-not-allowed",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={state === "disabled"}
      className={`${base} ${styles[state || "idle"]}`}
    >
      {text}
    </button>
  );
}

export default function QuizQuestion({
  question,    // { image, correctAnswer, options, famille }
  selected,    // string | null
  onAnswer,
  onNext,
  hasNext,
}) {
  if (!question) return null;

  const { image, correctAnswer, options, famille } = question;

  const getState = (opt) => {
    if (!selected) return "idle";
    if (opt === correctAnswer) return "correct";
    if (opt === selected && selected !== correctAnswer) return "wrong";
    return "disabled";
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
      {/* IMAGE — ratio fixe + taille réduite sur mobile */}
      <div className="lg:col-span-2">
        <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50 dark:bg-slate-900/40">
          {/* On contraint la largeur pour réduire la hauteur (marges latérales ok) */}
          <div
            className="mx-auto w-full max-w-[260px] sm:max-w-[320px] md:max-w-[420px] lg:max-w-none"
            style={{ aspectRatio: "551 / 617" }}
          >
            <img
              src={image}
              alt={`Technique ${famille || ""}`}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          </div>
        </div>
        {famille && (
          <div className="mt-2 text-xs text-slate-600 dark:text-slate-300 text-center lg:text-left">
            Famille : <span className="font-medium">{famille}</span>
          </div>
        )}
      </div>

      {/* OPTIONS + CTA */}
      <div className="space-y-3">
        {options.map((opt) => (
          <OptionButton
            key={opt}
            text={opt}
            onClick={() => onAnswer?.(opt)}
            state={getState(opt)}
          />
        ))}

        {/* CTA : dégradé, toujours visible, état disabled explicite */}
        <div className="pt-2">
          <button
            type="button"
            onClick={onNext}
            disabled={!selected}
            className={[
              "group w-full relative overflow-hidden rounded-2xl px-5 py-3",
              "font-semibold text-white shadow-lg transition",
              "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-400",
              selected
                ? "bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 hover:shadow-xl"
                : "bg-slate-300 dark:bg-slate-800 text-white/80 cursor-not-allowed",
            ].join(" ")}
            title={selected ? (hasNext ? "Question suivante" : "Voir les résultats") : "Choisis une réponse"}
          >
            <span className="inline-flex items-center justify-center gap-2">
              {hasNext ? (
                <>
                  <span>Question suivante</span>
                  <FaChevronRight className={selected ? "transition-transform group-hover:translate-x-0.5" : ""} />
                </>
              ) : (
                <>
                  <span>Voir les résultats</span>
                  <FaFlagCheckered />
                </>
              )}
            </span>
            {selected && (
              <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity bg-white" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
